/* Reading her locked Curriculum out of My Offer, so Course can start from it.
 *
 * WHERE THIS SITS
 * The curriculum stays in My Offer. It is not moved here and it is never written
 * back — it's the thing she markets from, and My Positioning, the Website and
 * social copy all read it. Course SEEDS from it, the way the Offer Ladder pulls
 * her Signature Offer and a website page splits from its locked copy.
 *
 * WHY THIS ONE CAN BE PARSED AND `offerStore` COULDN'T
 * The My Offer guide makes Brill emit the curriculum in a fixed shape
 * (api/_lib/prompts.js, prompt 7 of the `offer` guide):
 *
 *   WEEK 1 — Foundation & Commitment
 *   Outcome: She leaves with her four-quadrant vision built.
 *   Homework: Draft the vision, all four quadrants, specifics not aspirations.
 *
 * That's a stated format, not a layout Brill drifts through — so a header regex
 * and two labelled lines is honest reading, not the guesswork that produced three
 * wrong-data bugs in the offer pull. It still refuses to infer: a week with no
 * Outcome line gets an empty outcome for her to fill, never a sentence lifted out
 * of the prose nearby.
 *
 * WEEK NUMBERS BECOME ORDER. She dropped weeks from Course entirely — the number
 * in the source is a position, and the word "week" never surfaces in the panel.
 */

const CURRICULUM_SECTION = "Your Curriculum";

/* A week header. Tolerates what Brill actually varies: `WEEK 1 —`, `Week 1 -`,
   `WEEK 1:`, a leading `#` or `**`, and a title that may be empty. The number is
   required — without one this is prose, not a week. */
const HEADER = /^[ \t]*[#>*\s]*week[ \t]+(\d+)[ \t]*[*]*[ \t]*(?:[—–\-:.)]+[ \t]*)?(.*)$/i;

/* The labelled lines inside a week. A value runs to the next label or the next
   week, so a multi-line Outcome keeps its words. */
const FIELDS = ["outcome", "homework"];
const LABEL = /^[ \t]*[*_]*\s*(outcome|homework)\s*[*_]*[ \t]*:[ \t]*(.*)$/i;

function clean(text) {
  return String(text || "").replace(/[*_`]/g, "").replace(/\s+/g, " ").trim();
}

/** The locked curriculum text, or "" if she hasn't locked one. */
export function curriculumText(offerOutputs = {}) {
  return String(offerOutputs?.[CURRICULUM_SECTION] || "").trim();
}

/** Is there a curriculum to pull? Gates the automatic seed — creating empty
 *  modules the moment she opens the panel is clutter, not a head start. */
export function hasLockedCurriculum(offerOutputs = {}) {
  return !!curriculumText(offerOutputs);
}

/**
 * The modules in her locked curriculum, in order.
 *
 * Returns `[{ order, title, outcome, homework }]`, or [] when there's nothing to
 * read. `order` is the week number the source gave, which is what makes a
 * curriculum that skips or repeats a number still come out in the order she
 * wrote it — the position is renumbered from the list, not trusted from the text.
 */
export function parseCurriculum(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const lines = raw.split(/\r?\n/);
  const modules = [];
  let current = null;
  let label = null;

  const push = (value) => {
    if (!current || !label) return;
    current[label] = current[label] ? `${current[label]}\n${value}` : value;
  };

  for (const line of lines) {
    const head = HEADER.exec(line);
    if (head) {
      current = { order: Number(head[1]), title: clean(head[2]), outcome: "", homework: "" };
      modules.push(current);
      label = null;
      continue;
    }
    if (!current) continue;   // anything above the first week is preamble

    const field = LABEL.exec(line);
    if (field) {
      label = field[1].toLowerCase();
      current[label] = clean(field[2]);
      continue;
    }
    // A blank line ends a value: the next paragraph belongs to the week, not to
    // the label. Without this an Outcome would swallow everything under it.
    if (!line.trim()) { label = null; continue; }
    push(clean(line));
  }

  return modules
    .filter((m) => m.title || m.outcome || m.homework)
    .map((m, i) => ({
      ...m,
      outcome: clean(m.outcome),
      homework: clean(m.homework),
      // Renumbered from position. A curriculum that jumps 1, 2, 4 is still three
      // modules in a row, and a gap she never wrote is not a module.
      order: i,
      // Kept so a module can say which week of her curriculum it came from.
      sourceWeek: Number.isFinite(m.order) ? m.order : null,
    }));
}

/** Everything in one call: her locked curriculum, read as modules. */
export function curriculumModules(offerOutputs = {}) {
  return parseCurriculum(curriculumText(offerOutputs));
}

/** Which blocks a seeded module arrives with. The curriculum states an outcome
 *  and a homework and nothing else, so those are the only two blocks that exist —
 *  the outline, questions and pre-work are what Brill drafts next, and listing
 *  them empty would make every module read as half-broken rather than started. */
export function seededBlockOrder(module) {
  return ["outcome", "homework"].filter((k) => String(module?.[k] || "").trim());
}
